import { Box, Button, DialogContent, DialogTitle, Divider } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { ListItemData } from "@entrilia/shared/components/inputs/CheckItemsList";
import CheckItemsList from "@entrilia/shared/components/inputs/CheckItemsList";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import type { CompanyWithAccess } from "../definitions";

interface Props {
  open: boolean;
  selectedCompanies: CompanyWithAccess[];
  availableCompanies: ListItemData[];
  onClose: (selectedCompanies?: CompanyWithAccess[]) => void;
}

const CompaniesSelectDialog = ({ open, onClose, availableCompanies, selectedCompanies }: Props) => {
  const [searchFilter, setSearchFilter] = useState("");
  const [companiesToAdd, setCompaniesToAdd] = useState<string[]>([]);

  const clientsCompaniesList = useMemo<ListItemData[]>(() => {
    const query = searchFilter.trim().toLowerCase();
    if (!query) return availableCompanies;
    return availableCompanies.filter(
      (client) => client.label.toLowerCase().includes(query) || client.value.toLowerCase().includes(query)
    );
  }, [availableCompanies, searchFilter]);

  const selectedCompaniesList = useMemo(
    () => clientsCompaniesList.filter((company) => selectedCompanies.map((c) => c.clientCode).includes(company.value)),
    [clientsCompaniesList, selectedCompanies]
  );

  const handleSelectedCompanies = useCallback(
    (companies: ListItemData[]) => {
      setCompaniesToAdd(
        companies
          .map((company) => company.value)
          .filter((value: string) => !selectedCompanies.map((c) => c.clientCode).includes(value))
      );
    },
    [selectedCompanies]
  );

  const handleClose = (selectedCompanies: CompanyWithAccess[] | undefined) => {
    onClose(selectedCompanies);
    setTimeout(() => {
      setCompaniesToAdd([]);
      setSearchFilter("");
    }, 250);
  };

  const addSelectedCompanies = () => {
    const companiesAdded: CompanyWithAccess[] = companiesToAdd.map((clientCode) => ({
      clientCode,
      roles: [],
      entityIds: [],
      accessToAllEntities: false,
    }));

    handleClose([...(selectedCompanies || []), ...companiesAdded]);
  };

  return (
    <SmallDialog open={open} scroll="paper" sx={{ "& .MuiDialog-paper": { height: "80vh" } }}>
      <DialogTitle>Companies</DialogTitle>
      <DialogCloseButton onClick={() => handleClose(undefined)} />

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ m: 0, px: 2.5, pb: 2, pt: 0.375 }}>
          <SearchField onSearch={setSearchFilter} placeholder="Enter company name" fullWidth />
        </Box>
        <Divider />
        <CheckItemsList
          managed
          primaryTypographyVariant="subtitle1"
          items={clientsCompaniesList}
          onSelectedChanged={handleSelectedCompanies}
          preselectedItems={selectedCompaniesList}
          disabledItems={selectedCompaniesList}
        />
      </DialogContent>
      <Divider />

      <DialogButtons onCancel={() => handleClose(undefined)}>
        <Button disabled={companiesToAdd.length === 0} variant="contained" onClick={addSelectedCompanies}>
          {`Add${!companiesToAdd.length ? "" : " " + companiesToAdd.length} ${
            companiesToAdd.length === 1 ? "Company" : "Companies"
          }`}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default CompaniesSelectDialog;
